import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import { appRoutes } from "lib/appRoutes";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { useTracking } from "lib/tracking";
import { LeadContactCard } from "./LeadContactCard";

/** The channels a reveal would return, in the order the contacts table stacks them. */
const LOCKED_CHANNELS: { key: string; icon: IconProps["icon"]; label: string }[] = [
  // The name leads: it is the one field the card's headline had to give up, so it is also the
  // first thing a reveal would hand back.
  { key: "name", icon: "user01", label: "name" },
  { key: "mobile", icon: "phone01", label: "mobile phone" },
  { key: "email", icon: "mail01", label: "email" },
  { key: "linkedin", icon: "linkedin", label: "LinkedIn" },
];

/**
 * Seniority arrives as its raw tier key for an unentitled company: the label map comes from
 * `useSeniorityTierOptions`, whose resolver requires CONTACTS. Humanise the key rather than show
 * `individual_contributor` verbatim — close to the entitled label without the extra request.
 */
function humaniseSeniority(seniority: string | null | undefined): string {
  const raw = seniority?.trim();
  if (!raw) return "-";
  const spaced = raw.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Recommended contacts as a company without CONTACTS access sees them: the lead's real
 * recommendations, with every identifying field withheld by the server (see `getSuggestedContacts`
 * — `fullName`, `email`, `phone`, `contactId`, `redeemId` and the LLM-written `reason` never reach
 * an unentitled client). What is left is enough to show the shape of the answer without naming
 * anyone: the role, its seniority and function, and the buyer it sits in.
 *
 * The card is the entitled one with its actions removed: the row still expands to the contact's
 * attributes, and the details column names each channel a reveal would return. Revealing here
 * opens the upgrade modal rather than spending a credit. The job title takes the headline slot in
 * place of the name.
 */
export function TeasedContactsCard({
  suggestedContacts,
}: {
  suggestedContacts: NonNullable<LeadDetails>["suggestedContacts"];
}) {
  const { openPaywallModal } = usePaywall();
  // The paywall logs from its own provider, so the card's context is handed over explicitly.
  const { trackingData } = useTracking();

  return (
    <Flex column gap={12}>
      {suggestedContacts.map((suggestion) => (
        <LeadContactCard
          key={suggestion.id}
          variant="suggested"
          ariaLabel="Recommended contact (locked)"
          // The headline is the role, not a person — see the note above.
          name={suggestion.jobTitle || "Decision maker"}
          seniority={humaniseSeniority(suggestion.seniority)}
          jobFunction={suggestion.jobFunction?.length ? suggestion.jobFunction.join(", ") : "-"}
          buyer={suggestion.buyer ?? null}
          // Accept and dismiss belong to the entitled card: there is nothing to attach or rank
          // until the company has access.
          actions={null}
          details={
            <LockedChannels onReveal={() => openPaywallModal("CONTACTS", { trackingData })} />
          }
        />
      ))}
    </Flex>
  );
}

/**
 * The details column for a contact this company cannot see: one row per channel a reveal would
 * return, each naming its own data type so the offer is concrete rather than a generic upsell.
 * Every row routes to the upgrade modal.
 */
function LockedChannels({ onReveal }: { onReveal: () => void }) {
  return (
    <LockedChannelList column gap={4}>
      {LOCKED_CHANNELS.map((channel) => (
        <Flex key={channel.key} alignItems="center" gap={8}>
          <UIcon icon={channel.icon} size={16} color="sysTextPlaceholder" />
          {/* The shared text link, rendered unauthorised: same weight, colour and hover as the
              buyer link above it, and a button rather than navigation. It points at contacts
              because that is where an entitled user would go — unentitled, it opens the upgrade
              modal instead, exactly as the section's "Find contacts" button does. */}
          <RouterTextLink {...appRoutes.contacts} authorised={false} onUnauthorisedClick={onReveal}>
            Reveal {channel.label}
          </RouterTextLink>
        </Flex>
      ))}
    </LockedChannelList>
  );
}

/**
 * Each prompt stands in for a contact value, so it reads at the size and weight of the real ones
 * on an entitled card rather than at the button's own. Its colour deliberately does not follow:
 * link blue is the one thing in the card asking to be clicked.
 *
 * The prompt renders as an antd link-button (the shared link component's unauthorised form), which
 * sets its own size and weight from a single class, so this has to outrank it.
 */
const LockedChannelList = styled(Flex)({
  button: { fontSize: "inherit", fontWeight: 500 },
});
